import { Injectable } from '@angular/core';
import { EyePosition } from 'src/app/patients/patient/patient-chart/patient-chart.component';

import { CustomPdfConfig, FilterFields } from './pdf-custom-export.model';
type EyePositionMap<T> = {
  [k in EyePosition]?: T;
};
type A = { [key: string]: string };

@Injectable({
  providedIn: 'root'
})
export class CustomPdfExportService {
  public customPdfData = {};
  public lineChartLegend = [];
  public assessmentType: string = '';
  public interactionSignoffGroup: any = '';
  public patientChartImages: EyePositionMap<any | null> = {};

  public data = {};

  constructor() {}

  configuration(config: CustomPdfConfig, parentData: any, layoutService: any) {
    if (!parentData) return null;

    let processedData = this.processFilter(parentData, config.filterType);
    const validators = config && config.validators ? config.validators : null;
    processedData = this.filterDefaultData(processedData, validators);

    return config.transform ? config.transform(processedData, layoutService) : processedData;
  }

  processFilter(parentData: any, filter: FilterFields[]) {
    return filter.reduce((acc, filter) => {
      return this.applyFilter(parentData, filter);
    }, parentData);
  }

  applyFilter(parentData: any, filter: FilterFields) {
    if (!parentData) return null;
    switch (filter.type) {
      case 'include':
        return this.prefixFilter(parentData, filter.value);

      case 'exclude':
        return this.excludeFilter(parentData, filter.pattern);

      case 'ignore':
        return this.ignoreFilter(parentData, filter.keys, filter.matchStrategy || 'full');

      case 'composite':
        return this.compositeFilter(parentData, filter);
      case 'exists':
        return this.existsFilter(parentData, filter.keys);
      case 'custom':
        return this.customFilter(parentData, filter.handler);
      default:
        return parentData;
    }
  }

  private customFilter(parentData: any, filterFunction: Function) {
    return filterFunction('', parentData);
  }

  private prefixFilter(parentData: any, prefix: string) {
    const check = (value: string) => value.toLowerCase().includes(prefix.toLowerCase());
    return Object.keys(parentData)
      .filter(check)
      .reduce((acc, key) => ({ ...acc, [key]: parentData[key] }), {});
  }

  private excludeFilter(data: any, pattern: string) {
    return Object.keys(data)
      .filter(value => !value.includes(pattern))
      .reduce((acc, key) => ({ ...acc, [key]: data[key] }), {});
  }

  private ignoreFilter(parentData: any, ignoreKeys: Set<string>, matchStrategy = 'exact') {
    const matchStrategyType =
      matchStrategy === 'exact'
        ? (value: string) => !ignoreKeys.has(value)
        : (value: string) => ![...ignoreKeys].some(key => key.includes(value));
    return Object.keys(parentData)
      .filter(matchStrategyType)
      .reduce((acc, key) => ({ ...acc, [key]: parentData[key] }), {});
  }
  private compositeFilter(
    parentData: any,
    filter: { type: 'composite'; operator: 'AND' | 'OR'; filterField: FilterFields[] }
  ) {
    const check =
      filter.operator === 'AND'
        ? (key: string) => filter.filterField.every(item => this.filters(key, parentData, item))
        : (key: string) => filter.filterField.some(item => this.filters(key, parentData, item));
  }

  private filters(key: string, value: unknown, filter: FilterFields) {
    return Object.keys(this.applyFilter({ [key]: value }, filter)).length > 0;
  }

  private filterDefaultData(parentData: any, validator?: CustomPdfConfig['validators']) {
    if (!validator) return parentData;

    return Object.keys(parentData)
      .filter(key => {
        return validator.checkIfDefault(key, parentData);
      })
      .reduce((acc, key) => ({ ...acc, [key]: parentData[key] }), {});
  }

  private existsFilter(parentData, keys: Set<string>) {
    const exist = (value: string) => keys.has(value.toLowerCase());
    return Object.keys(parentData)
      .filter(exist)
      .reduce((acc, key) => ({ ...acc, [key]: parentData[key] }), {});
  }
}
